/**
 * Withdrawal strategy editor (spec §7): strategy choice, ordering mode
 * (including bracket-aware), and the notes the spec asks for.
 */
import type { Plan, TaxFamily } from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import { CurrencyField, NumberField } from './Inputs';

interface Props {
  plan: Plan;
  update: (mut: (p: Plan) => void) => void;
}

type OrderPreset = 'traditional' | 'pretax_first' | 'roth_first' | 'bracket_aware';

const ORDER_PRESETS: Record<Exclude<OrderPreset, 'traditional' | 'bracket_aware'>, TaxFamily[]> = {
  pretax_first: ['pretax', 'aftertax', 'roth', 'hsa'],
  roth_first: ['roth', 'aftertax', 'pretax', 'hsa'],
};

function currentPreset(plan: Plan): OrderPreset {
  const d = plan.decisions;
  if (d.orderMode === 'bracket_aware') return 'bracket_aware';
  if (d.orderMode === 'custom' && d.customOrder?.length) {
    if (d.customOrder[0] === 'pretax') return 'pretax_first';
    if (d.customOrder[0] === 'roth') return 'roth_first';
  }
  return 'traditional';
}

export function StrategyTab({ plan, update }: Props) {
  const d = plan.decisions;
  const preset = currentPreset(plan);

  return (
    <div>
      <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        How much to withdraw
      </h3>
      <div className="seg" role="group" aria-label="Withdrawal strategy" style={{ marginBottom: 'var(--space-3)' }}>
        <button aria-pressed={d.withdrawalStrategy === 'fund_spending'}
          onClick={() => update((p) => { p.decisions.withdrawalStrategy = 'fund_spending'; })}>
          Fund my spending
        </button>
        <button aria-pressed={d.withdrawalStrategy === 'fixed_percentage'}
          onClick={() => update((p) => {
            p.decisions.withdrawalStrategy = 'fixed_percentage';
            p.decisions.fixedPercentage ??= 0.04;
          })}>
          Fixed percentage
        </button>
      </div>
      {d.withdrawalStrategy === 'fund_spending' && (
        <p className="hint">
          Withdraw whatever the budget requires (the default). The "What can I actually afford?"
          card below the charts answers the reverse question — the most spending this plan
          sustains to the end.
        </p>
      )}
      {d.withdrawalStrategy === 'fixed_percentage' && (
        <div className="inputs-grid">
          <NumberField label="Withdraw % of portfolio per year" min={1} max={12}
            value={Math.round((d.fixedPercentage ?? 0.04) * 100)}
            hint="Spending flexes to whatever the rule produces (the classic 4%-style rule)"
            onCommit={(v) => update((p) => { p.decisions.fixedPercentage = v / 100; })} />
        </div>
      )}

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Which accounts to tap first
      </h3>
      <div className="seg" role="group" aria-label="Withdrawal order" style={{ marginBottom: 'var(--space-3)' }}>
        {(['traditional', 'pretax_first', 'roth_first', 'bracket_aware'] as OrderPreset[]).map((o) => (
          <button key={o} aria-pressed={preset === o}
            onClick={() => update((p) => {
              if (o === 'traditional') {
                p.decisions.orderMode = 'traditional';
                p.decisions.customOrder = undefined;
              } else if (o === 'bracket_aware') {
                p.decisions.orderMode = 'bracket_aware';
                p.decisions.bracketAwareCeiling ??= 0.22;
              } else {
                p.decisions.orderMode = 'custom';
                p.decisions.customOrder = [...ORDER_PRESETS[o]];
              }
            })}>
            {o === 'traditional' ? 'Traditional' : o === 'pretax_first' ? 'Pre-tax first'
              : o === 'roth_first' ? 'Roth first' : 'Bracket-aware'}
          </button>
        ))}
      </div>
      {preset === 'traditional' && (
        <p className="hint">After-tax → pre-tax → Roth → HSA. The conventional order.</p>
      )}
      {preset === 'bracket_aware' && (
        <>
          <div className="inputs-grid">
            <div className="field">
              <label>
                Fill pre-tax withdrawals to the top of
                <select value={String(d.bracketAwareCeiling ?? 0.22)}
                  onChange={(e) => update((p) => { p.decisions.bracketAwareCeiling = Number(e.target.value); })}>
                  <option value="0.1">the 10% bracket</option>
                  <option value="0.12">the 12% bracket</option>
                  <option value="0.22">the 22% bracket</option>
                  <option value="0.24">the 24% bracket</option>
                </select>
              </label>
            </div>
          </div>
          <p className="hint">
            Each year: pre-tax withdrawals fill ordinary income to the top of the chosen bracket
            (cheap dollars out at a known rate), the remainder comes from after-tax, and Roth is
            preserved for last. Usually beats both the traditional and reversed orders on
            lifetime taxes.
          </p>
        </>
      )}
      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Early-withdrawal penalties
      </h3>
      <label className="field-check" style={{ marginBottom: 'var(--space-3)' }}>
        <input type="checkbox" checked={d.avoidEarlyPenalties ?? true}
          onChange={(e) => update((p) => { p.decisions.avoidEarlyPenalties = e.target.checked; })} />
        <span>
          <strong>Spend penalty-free money first (recommended)</strong>
          <span className="hint" style={{ display: 'block' }}>
            Before 59½ most retirement accounts cost 10% to touch. With this on, accounts that
            would be penalized wait until every clean source is spent — Roth contributions,
            seasoned conversions, a 457(b) after you separate, or a plan covered by the Rule of
            55. The penalty is still paid rather than letting the plan fail, so anything left
            is a real gap in your bridge to 59½.
          </span>
          <span className="hint" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            <strong>Straight talk:</strong> this is a trade, not a free win. Dodging the penalty
            means spending Roth money early, which forfeits decades of tax-free growth and
            leaves the pre-tax balance to compound into bigger RMDs later. For a plan that was
            never going to run out, the 10% is often the cheaper of the two — in testing it can
            end with less. Where it reliably helps is a strained plan: it stretches how long the
            money lasts. Toggle it and compare the two numbers for <em>your</em> plan.
          </span>
        </span>
      </label>

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Safety bucket
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        A cash-like account (T-bills, money market) held as sequence-of-returns insurance
        during the first years of retirement. It is protected from normal withdrawals; after a
        down market year, spending comes from the bucket instead of selling depressed
        investments, and it refills gradually once markets recover. For early retirees it can
        also fund the pre-Medicare years — cash draws realize almost no gains, keeping income
        low enough to preserve the ACA subsidy. Straight talk: with the same overall
        allocation, the market benefit is roughly a wash in simulation (protection and cash
        drag offset — the research says the same). The dependable wins are the ACA subsidy and
        never being forced to sell into a crash.
      </p>
      <div className="inputs-grid">
        <div className="field">
          <label>
            Bucket account
            <select
              value={plan.decisions.bucketStrategy?.accountId ?? ''}
              onChange={(e) => update((p) => {
                const id = e.target.value;
                if (!id) {
                  p.decisions.bucketStrategy = undefined;
                } else {
                  p.decisions.bucketStrategy = {
                    accountId: id,
                    targetToday: p.decisions.bucketStrategy?.targetToday ?? 250_000,
                    protectAcaYears: p.decisions.bucketStrategy?.protectAcaYears ?? true,
                  };
                }
              })}
            >
              <option value="">None — no bucket strategy</option>
              {plan.accounts
                .filter((a) => ACCOUNT_META[a.type].taxFamily === 'aftertax')
                .map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
            </select>
          </label>
          <span className="hint">
            Pick (or first create, under Accounts) a cash / money-market / treasury account
          </span>
        </div>
        {plan.decisions.bucketStrategy && (
          <>
            <CurrencyField
              label="Target bucket size (today's $)"
              value={plan.decisions.bucketStrategy.targetToday}
              hint="2-4 years of spending is the common range"
              onCommit={(v) => update((p) => { p.decisions.bucketStrategy!.targetToday = v; })}
            />
            <div className="field">
              <label>
                Also fund the pre-Medicare years from it?
                <select
                  value={(plan.decisions.bucketStrategy.protectAcaYears ?? true) ? 'yes' : 'no'}
                  onChange={(e) => update((p) => {
                    p.decisions.bucketStrategy!.protectAcaYears = e.target.value === 'yes';
                  })}
                >
                  <option value="yes">Yes — protect the ACA subsidy</option>
                  <option value="no">No — down-market years only</option>
                </select>
              </label>
              <span className="hint">
                Bucket draws barely raise MAGI, so the health-insurance credit survives the
                bridge to 65
              </span>
            </div>
            <NumberField
              label="Active for how many years after retiring"
              min={3}
              max={25}
              value={plan.decisions.bucketStrategy.windowYears ?? 10}
              hint="Sequence risk lives in the first decade; afterwards the bucket winds down and gets spent normally — holding big cash forever costs more than it protects"
              onCommit={(v) => update((p) => { p.decisions.bucketStrategy!.windowYears = v; })}
            />
          </>
        )}
      </div>

      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Tax-loss harvesting
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Selling losing positions (and immediately buying similar ones) to bank capital losses.
        Losses offset realized gains without limit, then up to $3,000/yr of other income, and
        the rest carries forward. The model is honest about the catch: every harvested dollar
        lowers your cost basis, so future sales realize bigger gains — taxes shift later, they
        don't vanish. Where it shines: bridge years before Medicare, where offsetting the gains
        you realize to live on keeps MAGI low and the ACA subsidy intact. Down-market harvests
        appear in the Monte Carlo and stress results; the baseline rate is what shows up in the
        main projection.
      </p>
      <div className="inputs-grid">
        <div className="field">
          <label>
            Harvest losses?
            <select
              value={plan.decisions.taxLossHarvesting?.enabled ? 'yes' : 'no'}
              onChange={(e) => update((p) => {
                p.decisions.taxLossHarvesting =
                  e.target.value === 'yes'
                    ? {
                        enabled: true,
                        downYearCapture: p.decisions.taxLossHarvesting?.downYearCapture ?? 0.75,
                        baselinePct: p.decisions.taxLossHarvesting?.baselinePct ?? 0.01,
                      }
                    : undefined;
              })}
            >
              <option value="no">No</option>
              <option value="yes">Yes — I harvest losses in my taxable account</option>
            </select>
          </label>
          <span className="hint">Mind the wash-sale rule in real life: no rebuying the same fund within 30 days, IRAs included</span>
        </div>
        {plan.decisions.taxLossHarvesting?.enabled && (
          <>
            <NumberField
              label="Typical year: harvest % of brokerage"
              min={0} max={5}
              value={Math.round((plan.decisions.taxLossHarvesting.baselinePct ?? 0.01) * 100)}
              hint="Lot-level harvesters and direct indexers capture ~1-2% even in up markets; 0 = only in crashes"
              onCommit={(v) => update((p) => {
                p.decisions.taxLossHarvesting!.baselinePct = v / 100;
              })}
            />
            <NumberField
              label="Crash years: % of the decline captured"
              min={0} max={100}
              value={Math.round((plan.decisions.taxLossHarvesting.downYearCapture ?? 0.75) * 100)}
              hint="You can rarely capture all of it — 75% is a realistic default"
              onCommit={(v) => update((p) => {
                p.decisions.taxLossHarvesting!.downYearCapture = v / 100;
              })}
            />
          </>
        )}
      </div>

      <p className="hint" style={{ marginTop: 'var(--space-4)' }}>
        Early-retiree note: 457(b) accounts have no 10% pre-59½ penalty after leaving the job —
        the engine knows. Substantially-equal-periodic-payments (72(t)/SEPP) schedules are a
        real option for penalty-free early access but aren't modeled in this version.
      </p>
    </div>
  );
}
